import React, { createContext, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { SidePanelProvider, SidePanelProviderProps } from './types'
import { debounce } from '../../../../shared/helpers'
import { SIDE_PANEL_ITEMS, defaultContextValue, isAllowedItem } from './utils'
import {
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'

const { LIVESTREAM, MY_STATION, SEARCH } = SIDE_PANEL_ITEMS

const SidePanelContext = createContext<SidePanelProvider>(defaultContextValue)

function useSidePanelProvider(): SidePanelProvider {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [isSidePanelFocused, setIsSidePanelFocused] = useState(true)
  const [forceSidePanelOpenedUnfocused, setForceSidePanelOpenedUnfocused] =
    useState(false)
  const [activeItem, setActiveItem] = useState('')
  const [keepSidePanelOpened, setKeepSidePanelOpened] = useState(false)
  const [keepLogoVisible, setKeepLogoVisible] = useState(false)

  useEffect(() => {
    const currentFocusKey = getCurrentFocusKey()
    if (
      isAllowedItem(activeItem) &&
      activeItem !== currentFocusKey &&
      !forceSidePanelOpenedUnfocused
    ) {
      setFocus(activeItem)
    }
  }, [activeItem, forceSidePanelOpenedUnfocused])

  useEffect(() => setActiveItem(pathname.replace('/', '')), [pathname])

  const onItemFocus = debounce((to) => {
    const toPath = `/${to}`
    if (pathname !== toPath) {
      navigate(toPath)
    }
  }, 1000)

  const handleForceSidePanelOpenedUnfocused = (opened: boolean) => {
    setForceSidePanelOpenedUnfocused(opened)
    setIsSidePanelFocused(!opened)
  }

  const isSidePanelOpened =
    keepSidePanelOpened || isSidePanelFocused || forceSidePanelOpenedUnfocused

  return {
    hasFullWidthBackground: activeItem === MY_STATION.to,
    closeWithoutVerticalTranslate: [MY_STATION.to, SEARCH.to].includes(
      activeItem
    ),
    hasMainContentFullHeight: [LIVESTREAM.to, SEARCH.to].includes(activeItem),
    isSidePanelFocused,
    isSidePanelOpened,
    forceSidePanelOpenedUnfocused,
    activeItem,
    isLogoHidden: !(isSidePanelOpened || keepLogoVisible),
    setIsSidePanelFocused,
    setForceSidePanelOpenedUnfocused: handleForceSidePanelOpenedUnfocused,
    setActiveItem,
    onItemFocus,
    keepSidePanelOpened: setKeepSidePanelOpened,
    setKeepLogoVisible,
    focusSidePanel: () => setFocus('SIDE_PANEL'),
  }
}

export function useSidePanel(): SidePanelProvider {
  return useContext(SidePanelContext)
}

export function SidePanelProvider({ children }: SidePanelProviderProps) {
  const value = useSidePanelProvider()
  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  )
}
